/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ClearIcon from '@mui/icons-material/Clear';
import { ButtonBase, IconButton, ListItem } from '@mui/material';

import { Namespaces } from '../../i18n';
import { NotificationItemFrameOptions } from '../../types/notification/NotificationItemFrameOptions';
import { useNotificationItemFrameStyles } from './NotificationItemFrame.styles';

type NotificationItemFrameProps = NotificationItemFrameOptions & {
  isRead?: boolean;
  primaryText: string | React.ReactNode;
  secondaryText?: string | React.ReactNode;
  datetime?: string | React.ReactNode;
  firstButtonText?: string | React.ReactNode;
  onFirstButtonClick?: () => any;
  secondButtonText?: string | React.ReactNode;
  onSecondButtonClick?: () => any;
  id: string;
};

export function NotificationItemFrame(props: NotificationItemFrameProps) {
  const {
    isRead,
    setNotificationAsRead,
    datetime,
    primaryText,
    secondaryText,
    firstButtonText,
    onFirstButtonClick,
    onSecondButtonClick,
    secondButtonText,
    id,
    onRemoveBtnClick,
    onRootClick,
  } = props;
  const classes = useNotificationItemFrameStyles();

  const { t } = useTranslation(Namespaces.Common);

  const hasFirstButton = useMemo(
    () => !!firstButtonText && !!onFirstButtonClick,
    [firstButtonText, onFirstButtonClick],
  );
  const hasSecondButton = useMemo(
    () => !!secondButtonText && !!onSecondButtonClick,
    [onSecondButtonClick, secondButtonText],
  );

  const hasActionButtons = useMemo(() => hasFirstButton || hasSecondButton, [hasFirstButton, hasSecondButton]);

  const onRemoveNotificationClick = () => {
    onRemoveBtnClick && onRemoveBtnClick({ id });
  };
  const onClick = () => {
    onRootClick && onRootClick({ id });
  };

  return (
    <ListItem
      aria-label={t('label.notif_item')}
      component="li"
      button={false}
      classes={{ root: classes.root }}
      className={cx({
        [classes.notRead]: !isRead,
        [classes.withActions]: hasActionButtons,
      })}
      onMouseEnter={!isRead && setNotificationAsRead ? () => setNotificationAsRead({ id }) : undefined}
      onClick={onClick}
    >
      {onRemoveBtnClick && (
        <IconButton
          aria-label="remove notification"
          onClick={event => {
            event.stopPropagation();
            onRemoveNotificationClick();
          }}
          className={classes.clearBtn}
          size="large"
        >
          <ClearIcon fontSize="small" />
        </IconButton>
      )}
      <div className={classes.primaryText}>{primaryText}</div>
      <div className={classes.secondaryText}>
        {secondaryText}
        {datetime && <div className={classes.datetime}>{datetime}</div>}
      </div>
      {hasActionButtons && (
        <div className={classes.actions}>
          {hasFirstButton && (
            <ButtonBase
              className={cx(classes.actionButton, classes.firstButton)}
              onClick={event => {
                event.stopPropagation();
                onFirstButtonClick && onFirstButtonClick();
              }}
            >
              {firstButtonText}
            </ButtonBase>
          )}
          {hasSecondButton && (
            <ButtonBase
              className={cx(classes.actionButton, classes.secondButton)}
              onClick={event => {
                event.stopPropagation();
                onSecondButtonClick && onSecondButtonClick();
              }}
            >
              {secondButtonText}
            </ButtonBase>
          )}
        </div>
      )}
    </ListItem>
  );
}
